import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import pegasusDiverterService from '../services/pegasusDiverterService';
import Toggle from './shared/Toggle';
import ConfirmModal from './shared/ConfirmModal';
import './OxiDequeueAdmin.css';
import './AdminSyncConnectQueries.css';

// Admin › Infra › Pegasus Diverter.
//
// Some Rotana properties have migrated to the pipelines but still send Pegasus HTNG to the legacy stack and cannot be
// repointed yet. A source listed here is diverted: v1-hc-pegasus-enqueue passes the raw envelope to the hub's keyless
// endpoint (/pegasus/inbound), which resolves the pipeline from the message itself — the same way Pegasus connects
// directly once every property has migrated. The list lives on the LEGACY database (pegasus_diverter) because the legacy
// lambda reads it on every request — a source added or switched off here applies to its next message.
//
// Pegasus has no API key: a source is <chainCode>/<hotelCode>/<userName> (never the password). A source is accepted
// only when a hub Pegasus pipeline's credential carries the same userName, chain code and hotel code (otherwise the hub
// would refuse the property), and each row shows the hub pipeline its messages land in and the legacy connector it was
// set up on. Every change is on the audit trail.

const when = (v) => (v ? new Date(v).toLocaleString() : '—');
const errorOf = (e) => e.response?.data?.error?.message || e.response?.data?.message || e.message;
const sourceOf = (r) => `${r.chain_code || ''}/${r.hotel_code || ''}/${r.username || ''}`;

export default function AdminPegasusDiverter() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [checkedAt, setCheckedAt] = useState(null);
    const [busy, setBusy] = useState({});              // id -> true while a write is in flight
    const [form, setForm] = useState({ chainCode: '', hotelCode: '', username: '', note: '' });
    const [adding, setAdding] = useState(false);
    const [removing, setRemoving] = useState(null);
    const [noteEdit, setNoteEdit] = useState(null);    // { id, note }

    const load = useCallback(async () => {
        setError(null);
        try {
            const res = (await pegasusDiverterService.list()).data;
            setRows(res.rows || []);
            setCheckedAt(res.checkedAt);
        } catch (e) {
            setError(errorOf(e));
        } finally {
            setLoading(false);
        }
    }, []);
    useEffect(() => { load(); }, [load]);

    const withBusy = async (id, fn) => {
        setBusy(b => ({ ...b, [id]: true }));
        setError(null);
        try { await fn(); await load(); } catch (e) { setError(errorOf(e)); }
        finally { setBusy(b => { const n = { ...b }; delete n[id]; return n; }); }
    };

    const add = async (e) => {
        e.preventDefault();
        setAdding(true);
        setError(null);
        try {
            await pegasusDiverterService.create({
                chainCode: form.chainCode.trim(), hotelCode: form.hotelCode.trim(),
                username: form.username.trim(), note: form.note.trim() });
            setForm({ chainCode: '', hotelCode: '', username: '', note: '' });
            await load();
        } catch (err) {
            setError(errorOf(err));
        } finally {
            setAdding(false);
        }
    };

    const canAdd = form.chainCode.trim() && form.hotelCode.trim() && form.username.trim();
    const diverting = rows.filter(r => r.active === 'Y').length;

    return (
        <div className="acp-page">
            <div className="acp-header">
                <div>
                    <h1 className="acp-title">Pegasus Diverter</h1>
                    <p className="acp-subtitle">
                        Pegasus properties that still send HTNG to the legacy stack and cannot be repointed. A source that
                        is on here and switched on has its messages passed to the hub, which resolves the pipeline from the
                        message's own userName / password / chain code / hotel code. Changes apply to the next message.
                    </p>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <button className="acp-btn acp-btn-secondary" onClick={load}>Refresh</button>
                </div>
            </div>

            {error && <div className="os-msg os-msg-error" onClick={() => setError(null)}>{error}</div>}

            <div className="acp-card" style={{ marginBottom: '1rem' }}>
                <div className="acp-card-header"><span className="acp-card-title">Divert a Pegasus source</span></div>
                <form onSubmit={add} style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'center', padding: '1rem 1.5rem' }}>
                    <input className="odq-filter-select scq-mono" style={{ minWidth: '9rem' }} required autoComplete="off"
                           placeholder="Chain code" value={form.chainCode} onChange={e => setForm(f => ({ ...f, chainCode: e.target.value }))} />
                    <input className="odq-filter-select scq-mono" style={{ minWidth: '9rem' }} required autoComplete="off"
                           placeholder="Hotel code" value={form.hotelCode} onChange={e => setForm(f => ({ ...f, hotelCode: e.target.value }))} />
                    <input className="odq-filter-select scq-mono" style={{ minWidth: '12rem', flex: 1 }} required autoComplete="off"
                           placeholder="userName (HTNG header)" value={form.username} onChange={e => setForm(f => ({ ...f, username: e.target.value }))} />
                    <input className="odq-filter-select" style={{ minWidth: '14rem', flex: 1 }} maxLength={255}
                           placeholder="Note (optional) — e.g. which property and why"
                           value={form.note} onChange={e => setForm(f => ({ ...f, note: e.target.value }))} />
                    <button type="submit" className="acp-btn acp-btn-primary" disabled={adding || !canAdd}>
                        {adding ? 'Adding…' : 'Divert to hub'}
                    </button>
                </form>
                <p className="scq-meta" style={{ padding: '0 1.5rem 1rem', margin: 0 }}>
                    A hub Pegasus pipeline must already have a credential with this userName, chain code and hotel code
                    (INBOUNDUSERNAME / DEFAULTCHAINCODE / DEFAULTRESORT) — a source the hub cannot run is refused, because
                    diverting it would stop the property. The password is never entered here; the hub checks it per message.
                </p>
            </div>

            <div className="acp-card">
                <div className="acp-card-header" style={{ flexWrap: 'wrap', gap: '0.5rem' }}>
                    <span className="acp-card-title">{rows.length} source{rows.length === 1 ? '' : 's'} · {diverting} diverting</span>
                    {checkedAt && <span className="scq-meta" style={{ marginLeft: 'auto' }}>Updated {new Date(checkedAt).toLocaleTimeString()}</span>}
                </div>
                {loading && !rows.length ? <div className="acp-empty">Loading…</div>
                    : rows.length === 0 ? <div className="acp-empty">No Pegasus sources are diverted — every property is handled by the legacy stack.</div>
                    : (
                    <table className="acp-table">
                        <thead>
                            <tr>
                                <th>Diverting</th><th>Source (chain/hotel/user)</th><th>Hub pipeline</th><th>Legacy connector</th>
                                <th>Forwarded</th><th>Note</th><th>Changed</th><th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map(r => (
                                <tr key={r.id}>
                                    <td>
                                        <Toggle size="sm" on={r.active === 'Y'} onLabel="On" offLabel="Off" busy={!!busy[r.id]}
                                                title={r.active === 'Y' ? 'Stop diverting — the legacy stack handles the property again' : 'Divert to the hub'}
                                                onChange={(on) => withBusy(r.id, () => pegasusDiverterService.update(r.id, { active: on ? 'Y' : 'N' }))} />
                                    </td>
                                    <td className="scq-mono" style={{ whiteSpace: 'nowrap' }}>{sourceOf(r)}</td>
                                    <td>
                                        {(r.hub || []).length === 0
                                            ? <span className="scq-status scq-status-error" title="No hub Pegasus pipeline has a matching credential — the hub will refuse the property">not on the hub</span>
                                            : r.hub.map(h => (
                                                <div key={`${h.templateId}-${h.implementationId}`}>
                                                    <Link to={`/pipeline-templates/${h.templateId}`}>{h.templateName || `template ${h.templateId}`}</Link>
                                                    <div className="scq-meta">{[h.hotelName || `hotel ${h.hotelId}`, h.implementationStatus].filter(Boolean).join(' · ')}</div>
                                                </div>
                                            ))}
                                    </td>
                                    <td>
                                        {(r.legacy || []).length === 0 ? <span className="scq-meta">none with these codes</span>
                                            : r.legacy.map(l => (
                                                <div key={l.connectorId}>{l.connectorId} · {l.connectorName || '?'}
                                                    <div className="scq-meta">{[l.accountName, l.connectorStatus].filter(Boolean).join(' · ')}</div>
                                                </div>
                                            ))}
                                    </td>
                                    <td>{r.calls ? Number(r.calls).toLocaleString() : '—'}<div className="scq-meta">{r.lastForwarded ? when(r.lastForwarded) : 'not yet'}</div></td>
                                    <td style={{ maxWidth: '18rem' }}>
                                        {noteEdit && noteEdit.id === r.id ? (
                                            <form onSubmit={(e) => { e.preventDefault(); const n = noteEdit.note; setNoteEdit(null); withBusy(r.id, () => pegasusDiverterService.update(r.id, { note: n })); }}
                                                  style={{ display: 'flex', gap: '0.35rem' }}>
                                                <input className="odq-filter-select" autoFocus maxLength={255} value={noteEdit.note}
                                                       onChange={e => setNoteEdit({ id: r.id, note: e.target.value })} />
                                                <button type="submit" className="acp-btn acp-btn-primary acp-btn-sm">Save</button>
                                                <button type="button" className="acp-btn acp-btn-secondary acp-btn-sm" onClick={() => setNoteEdit(null)}>Cancel</button>
                                            </form>
                                        ) : (
                                            <>{r.note || <span className="scq-meta">—</span>}{' '}
                                                <button className="acp-btn acp-btn-secondary acp-btn-sm" onClick={() => setNoteEdit({ id: r.id, note: r.note || '' })}>Edit</button></>
                                        )}
                                    </td>
                                    <td>{when(r.updated_at)}<div className="scq-meta">{r.updated_by || r.created_by || ''}</div></td>
                                    <td><button className="acp-btn acp-btn-secondary acp-btn-sm" disabled={!!busy[r.id]} onClick={() => setRemoving(r)}>Remove</button></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {removing && (
                <ConfirmModal
                    title="Stop diverting this Pegasus source?"
                    message={`Source ${sourceOf(removing)} is removed from the diverter. Its next message is handled by the legacy stack again${(removing.legacy || []).length ? '' : ' — and no legacy connector matches these codes, so the legacy stack will refuse it'}.`}
                    confirmLabel="Remove"
                    danger
                    onCancel={() => setRemoving(null)}
                    onConfirm={() => { const r = removing; setRemoving(null); withBusy(r.id, () => pegasusDiverterService.remove(r.id)); }}
                />
            )}
        </div>
    );
}
